import type { CrawlJobStatusCountDTO } from '@liks/lac-svc-api/esm/models/index';
import { LiksTooltip } from '../../../components/liks/LiksTooltip/LiksTooltip';
import { formatNumber } from '../../../utils/formatUtils';
import {
  crawlStatusClassSuffix,
  sortCrawlJobStatusCounts,
} from '../crawlJobAnalyticsUtils';
import './CrawlStatusBar.css';

export type StatusCountItem = {
  status?: string | null;
  count?: number | null;
};

export type CrawlStatusBarProps = {
  statusCounts: StatusCountItem[];
  locale: string;
  resolveStatusLabel: (status: string | null | undefined) => string;
  ariaLabel: string;
  className?: string;
  /** Defaults to crawl-job status order. */
  sortCounts?: (items: StatusCountItem[]) => StatusCountItem[];
};

export function CrawlStatusBar({
  statusCounts,
  locale,
  resolveStatusLabel,
  ariaLabel,
  className,
  sortCounts = (items) =>
    sortCrawlJobStatusCounts(items as CrawlJobStatusCountDTO[]),
}: CrawlStatusBarProps) {
  const segments = sortCounts(statusCounts).filter(
    (item) => (item.count ?? 0) > 0,
  );

  const bar = (
    <div
      className={[
        'crawl-status-bar',
        segments.length === 0 && 'crawl-status-bar--empty',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-label={
        segments.length === 0
          ? ariaLabel
          : `${ariaLabel}. ${segments
              .map(
                (item) =>
                  `${resolveStatusLabel(item.status)}: ${formatNumber(item.count, locale)}`,
              )
              .join(', ')}`
      }
    >
      {segments.map((item) => {
        const count = item.count ?? 0;
        const suffix = crawlStatusClassSuffix(item.status);
        return (
          <span
            key={item.status ?? 'unknown'}
            className={`crawl-status-bar__segment crawl-status-bar__segment--${suffix}`}
          >
            {formatNumber(count, locale)}
          </span>
        );
      })}
    </div>
  );

  if (segments.length === 0) {
    return bar;
  }

  const tooltipContent = segments
    .map(
      (item) =>
        `${resolveStatusLabel(item.status)}: ${formatNumber(item.count, locale)}`,
    )
    .join('\n');

  return (
    <LiksTooltip content={tooltipContent} placement="top-end">
      {bar}
    </LiksTooltip>
  );
}
